// src/App.jsx
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { useAuth } from "./context/AuthContext";
import posthog from "posthog-js";
import ReactGA from "react-ga4";

// Pages & Components
import LoginPage from "./pages/LoginPage";
import ReportsPage from "./pages/ReportsPage";
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminCompanies = lazy(() => import("./pages/AdminCompanies"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const AdminAuthorizedReports = lazy(() => import("./pages/AdminAuthorizedReports"));
const AdminDepartments = lazy(() => import("./pages/AdminDepartments"));
const AdminDemoRequests = lazy(() => import("./pages/AdminDemoRequests"));
import ContactUsPage from "./pages/ContactUsPage";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./components/Dashboard";
import WhatsAppButton from "./components/WhatsAppButton.jsx";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import AnimatedLogo from "./components/AnimatedLogo.jsx";
import HomzReadyProjectPage from "./pages/HomzReadyPages/HomzReadyProjectPage.jsx";
import HomzReadyEmptyStatePage from "./pages/HomzReadyPages/HomzReadyEmptyStatePage.jsx";
// ✅ HomzReady Admin pages (lazy loaded)
const ServiceProvidersPage = lazy(() => import("./pages/HomzReadyPages/ServiceProvidersPage.jsx"));
const PropertyTypesPage = lazy(() => import("./pages/HomzReadyPages/PropertyTypesPage.jsx"));
const LocationsPage = lazy(() => import("./pages/HomzReadyPages/LocationsPage.jsx"));
const ServiceTypesPage = lazy(() => import("./pages/HomzReadyPages/ServiceTypesPage.jsx"));
const AreasPage = lazy(() => import("./pages/HomzReadyPages/AreasPage.jsx"));
const ProjectsPage = lazy(() => import("./pages/HomzReadyPages/ProjectsPage.jsx"));
const HomzReadyClients = lazy(() => import("./pages/HomzReadyPages/HomzReadyClients.jsx"));
const HomzReadyProjectsManager  = lazy(() => import("./pages/HomzReadyPages/HomzReadyProjectsManager.jsx"));



ReactGA.initialize("G-3D481TYY51");

function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview", { path: location.pathname });
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);
  return null;
}

function LayoutWrapper({ collapsed, handleToggleCollapse }) {
  const { user } = useAuth();
  return (
    <div className="flex min-h-screen w-full">
      {user && <Sidebar collapsed={collapsed} onToggleCollapse={handleToggleCollapse} />}
      <div
        className={`flex flex-col flex-grow h-screen transition-all duration-300 overflow-hidden ${
          user ? (collapsed ? "ml-[72px]" : "ml-[288px]") : ""
        }`}
      >
        {user && <Navbar />}
        <RouteChangeTracker />
        <div className="flex-grow overflow-hidden">
          <Outlet />
        </div>
        {user && <Footer />}
      </div>
    </div>
  );
}

function AppWrapper() {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (window.innerWidth < 1024) setCollapsed(true);
    posthog.capture("App Loaded", { loggedIn: !!user });
  }, []);

  const handleToggleCollapse = () => setCollapsed((prev) => !prev);

  // ✅ Determine if user's company indicates "HomzReady" (defensive against field shapes)
  const isHomzReadyCompany = (() => {
    if (!user) return false;
    const candidatesRaw = [
      user.company,
      user.company_id,
      user.companyId,
      user.company_name,
      user.company?.name,
      user.company_id?.name,
      user.companyId?.name,
    ].filter(Boolean);

    const normalized = candidatesRaw
      .map((v) =>
        typeof v === "string"
          ? v.trim().toLowerCase()
          : typeof v?.name === "string"
          ? v.name.trim().toLowerCase()
          : ""
      )
      .filter(Boolean);

    return normalized.some((v) => v === "homzready");
  })();

  const canSeeHomzReady = !!user && (user.role === "admin" || isHomzReadyCompany);

  const routes = [
    { path: "/", element: <LandingPage /> },
    {
      path: "/login",
      element: user ? <Navigate to="/reports/Dep/Report" /> : <LoginPage />,
    },
    { path: "/ContactUsPage", element: <ContactUsPage /> },
    { path: "/reports/:department", element: <ReportsPage fallbackToFirstReport /> },
        // HomzReady public/detail URL )
    { path: "/hmzrdy/:username/:projectName", element: <HomzReadyProjectPage /> },
    { path: "/homzready", element: <HomzReadyEmptyStatePage /> },
    { path: "/homzready/welcome", element: <HomzReadyEmptyStatePage /> }, 

    // App layout (requires auth for inner routes)
    {
      element: <LayoutWrapper collapsed={collapsed} handleToggleCollapse={handleToggleCollapse} />,
      children: user
        ? [
            // Regular report view
            { path: "/reports/:department/:report", element: <ReportsPage /> },



            // Admin Control — ONLY admin
            ...(user.role === "admin"
              ? [
                  { path: "/admin/users", element: <AdminUsers /> },
                  { path: "/admin/companies", element: <AdminCompanies /> },
                  { path: "/admin/reports", element: <AdminReports /> },
                  { path: "/admin/authorized-reports", element: <AdminAuthorizedReports /> },
                  { path: "/admin/departments", element: <AdminDepartments /> },
                  { path: "/admin/demo-requests", element: <AdminDemoRequests /> },
                ]
              : []),

            // HomzReady Admin — admin OR HomzReady company
            ...(canSeeHomzReady
              ? [
                  { path: "/homzready/homzready-clients", element: <HomzReadyClients /> },
                  { path: "/homzready/service-providers", element: <ServiceProvidersPage /> },
                  { path: "/homzready/property-types", element: <PropertyTypesPage /> },
                  { path: "/homzready/locations", element: <LocationsPage /> },
                  { path: "/homzready/service-types", element: <ServiceTypesPage /> },
                  { path: "/homzready/areas", element: <AreasPage /> },
                  { path: "/homzready/projects", element: <ProjectsPage /> },
                  { path: "/homzready/projectsManager", element: <HomzReadyProjectsManager /> },
                ]
              : []),
          ]
        : [],
    },

    { path: "*", element: <Navigate to="/" /> },
  ];

  const router = createBrowserRouter(routes, {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  });

  return (
    <>
      <Suspense
        fallback={
          <div className="text-center p-10">
            <AnimatedLogo />
          </div>
        }
      >
        <RouterProvider router={router} />
      </Suspense>
      <WhatsAppButton />
    </>
  );
}

export default AppWrapper;
